<template>
  <div class="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
    <div class="max-w-4xl mx-auto">
      <!-- 页面标题 -->
      <h1 class="text-4xl font-bold mb-8 text-center">手册和指南</h1>
      
      <!-- 书本目录样式的容器 -->
      <div class="bg-white shadow-lg rounded-lg overflow-hidden border border-gray-200">
      <!-- 目录页标题 -->
      <div class="bg-gray-100 border-b border-gray-200 py-6 px-8">
        <h2 class="text-2xl font-serif italic text-gray-800 text-center">目录</h2>
      </div>
      
      <!-- 目录内容 - 使用折叠面板展示最外层 -->
      <div class="p-4">
        <div v-for="(section, sectionIndex) in sections" :key="sectionIndex" class="mb-2">
          <!-- 折叠面板头部 -->
          <button
            @click="toggleSection(sectionIndex)"
            class="w-full flex items-center justify-between px-4 py-3 bg-white border border-gray-200 rounded-md hover:bg-gray-50 transition-colors text-left"
          >
            <span class="text-lg font-bold text-gray-800">{{ section.title }}</span>
            <span class="text-gray-600 transition-transform duration-300" :class="{ 'transform rotate-180': expandedSections[sectionIndex] }">
              <i class="fas fa-chevron-down"></i>
            </span>
          </button>
          
          <!-- 折叠面板内容 -->
          <Transition name="collapse">
            <div v-if="expandedSections[sectionIndex]" class="mt-1">
              <div class="border border-t-0 border-gray-200 rounded-b-md p-4 bg-gray-50">
                <!-- 递归渲染多级菜单 -->
                <MenuLevel
                  :items="getAllItems(section)"
                  :parent-index="sectionIndex + 1"
                  :level="1"
                  @download="openDownloadModal"
                />
              </div>
            </div>
          </Transition>
        </div>
      </div>
    </div>

    <!-- 下载弹窗 -->
    <Teleport to="body">
      <Transition name="modal">
        <div v-if="showModal" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div class="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 overflow-hidden">
            <!-- 弹窗头部 -->
            <div class="bg-black text-white p-4 flex justify-between items-center">
              <h3 class="text-xl font-bold">北境骑团云文档下载</h3>
              <button @click="closeModal" class="text-white hover:text-gray-300">
                <i class="fas fa-times"></i>
              </button>
            </div>
            
            <!-- 弹窗内容 -->
            <div class="p-6">
              <div class="mb-4">
                <label class="block text-sm font-medium text-gray-800 mb-1">下载链接</label>
                <a 
                  :href="currentDownload?.link || '#'" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  class="block w-full px-4 py-2 bg-white border border-gray-300 rounded-md text-gray-800 hover:bg-gray-50 transition-colors overflow-hidden text-overflow-ellipsis whitespace-nowrap"
                >
                  {{ currentDownload?.link || '#' }}
                </a>
              </div>
              
              <div class="mb-4">
                <label class="block text-sm font-medium text-gray-800 mb-1">提取码</label>
                <div class="px-4 py-2 bg-white border border-gray-300 rounded-md text-gray-800 overflow-hidden text-overflow-ellipsis whitespace-nowrap">
                  {{ currentDownload?.password || 'xxx' }}
                </div>
              </div>
              
              <div class="mt-6 pt-4 border-t border-gray-300">
                <p class="text-sm text-gray-600">
                  <i class="fas fa-info-circle text-blue-600 mr-1"></i>
                  文档存储于“蓝奏云”官方平台，在其页面可能存在广告，请注意并下载正确文件内容。
                </p>
              </div>
            </div>
            
            <!-- 弹窗底部 -->
            <div class="px-6 py-4 bg-black flex justify-end space-x-3">
              <button 
                @click="closeModal"
                class="px-4 py-2 border border-gray-700 rounded-md text-white hover:bg-gray-900 transition-colors"
              >
                关闭
              </button>
              <a 
                :href="currentDownload?.link || '#'" 
                target="_blank" 
                rel="noopener noreferrer"
                class="px-4 py-2 border border-gray-700 text-white rounded-md hover:bg-gray-900 transition-colors"
              >
                立即下载
              </a>
            </div>
          </div>
        </div>
      </Transition>
    </Teleport>
    </div>
  </div>
  
</template>

<script setup lang="ts">
import { ref, h, defineComponent, PropType } from 'vue';

// 类型定义
interface MenuItem {
  type: 'menu' | 'button';
  title: string;
  children?: MenuItem[];
  link?: string;
  password?: string;
}

interface Section {
  title: string;
  items?: MenuItem[];
  // 兼容旧格式
  subsections?: Array<{ title: string; children?: any[]; link?: string; password?: string }>;
  buttons?: Array<{ title: string; link: string; password: string }>;
}

interface DownloadItem {
  title: string;
  link: string;
  password: string;
}

// 递归菜单组件
const MenuLevel = defineComponent({
  name: 'MenuLevel',
  props: {
    items: {
      type: Array as PropType<MenuItem[]>,
      required: true
    },
    parentIndex: {
      type: [String, Number] as PropType<string | number>,
      required: true
    },
    level: {
      type: Number as PropType<number>,
      required: true
    }
  },
  emits: {
    download: (button: DownloadItem) => true
  },
  setup(props, { emit }) {
    const expandedItems = ref<Record<number, boolean>>({});
    
    const toggleItem = (index: number): void => {
      expandedItems.value[index] = !expandedItems.value[index];
    };
    
    const handleDownload = (button: DownloadItem): void => {
      emit('download', button);
    };
    
    return {
      expandedItems,
      toggleItem,
      handleDownload
    };
  },
  render() {
    const { items, parentIndex, level, expandedItems, toggleItem, handleDownload } = this;
    
    return h('div', {
      class: 'space-y-1'
    }, items.map((item, index) => {
      // 生成当前项的索引
      const currentIndex = typeof parentIndex === 'string' 
        ? `${parentIndex}.${index + 1}` 
        : `${parentIndex}.${index + 1}`;
      
      // 如果是菜单项并且有子项，渲染为可折叠的项目
      if (item.type === 'menu' && item.children && item.children.length > 0) {
        return h('div', {
          class: 'border-l-2 border-gray-200 pl-2 ml-2'
        }, [
          h('button', {
            class: 'w-full flex items-center justify-between py-2 px-3 hover:bg-gray-100 rounded-md transition-colors text-left',
            onClick: () => toggleItem(index)
          }, [
            h('span', {
              class: 'text-gray-700'
            }, item.title),
            h('span', {
              class: 'flex items-center gap-2'
            }, [
              h('span', {
                class: 'font-serif text-gray-500 text-sm'
              }, currentIndex),
              h('i', {
                class: `fas fa-chevron-down text-gray-400 transition-transform duration-300 ${expandedItems[index] ? 'transform rotate-180' : ''}`
              })
            ])
          ]),
          h('Transition', {
            name: 'collapse'
          }, [
            expandedItems[index] ? h(MenuLevel, {
              items: item.children,
              parentIndex: currentIndex,
              level: level + 1,
              onDownload: handleDownload
            }) : null
          ])
        ]);
      }
      // 如果是功能按钮
      else if (item.type === 'button') {
        return h('div', {
          class: 'border-l-2 border-gray-200 pl-2 ml-2'
        }, [
          h('button', {
            class: 'w-full flex items-center justify-between py-2 px-3 rounded-md text-left',
            onClick: () => handleDownload(item as DownloadItem)
          }, [
            h('div', {
              class: 'flex items-center'
            }, [
              h('span', {
                class: 'inline-block w-2 h-2 rounded-full bg-blue-500 mr-2'
              }),
              h('span', {
                class: 'text-blue-600 font-medium'
              }, item.title)
            ]),
            h('span', {
              class: 'flex items-center gap-2'
            }, [
              h('span', {
                class: 'font-serif text-gray-500 text-sm'
              }, currentIndex),
              h('i', {
                class: 'fas fa-download text-blue-500'
              })
            ])
          ])
        ]);
      }
      // 普通菜单项
      else {
        return h('div', {
          class: `border-l-2 border-gray-200 pl-2 ml-2`
        }, [
          h('div', {
            class: 'flex items-center justify-between py-2 px-3 hover:bg-gray-100 rounded-md transition-colors'
          }, [
            h('span', {
              class: 'text-gray-700'
            }, item.title),
            h('span', {
              class: 'font-serif text-gray-500 text-sm'
            }, currentIndex)
          ])
        ]);
      }
    }));
  }
});

// 展开的章节状态
const expandedSections = ref<Record<number, boolean>>({});

// 切换章节展开/收起状态
const toggleSection = (index: number): void => {
  expandedSections.value[index] = !expandedSections.value[index];
};

// 递归处理菜单项，确保所有项目都有正确的type属性
const processItems = (items: any[]): MenuItem[] => {
  return items.map(item => {
    // 确定项目类型：如果有明确的type属性则使用，否则根据是否有link属性判断
    const itemType: 'menu' | 'button' = item.type || (item.link ? 'button' : 'menu');
    
    // 如果是menu类型且有children，递归处理子项
    if (itemType === 'menu' && item.children && Array.isArray(item.children) && item.children.length > 0) {
      return {
        ...item,
        type: 'menu',
        children: processItems(item.children)
      } as MenuItem;
    }
    
    // 返回带有明确类型的项目
    return {
      ...item,
      type: itemType
    } as MenuItem;
  });
};

// 获取章节的所有项目（处理统一的数据格式）
const getAllItems = (section: Section): MenuItem[] => {
  // 如果是新的统一数据格式，直接返回items数组
  if (section.items && Array.isArray(section.items)) {
    return processItems(section.items);
  }
  
  // 兼容旧格式：合并subsections和buttons
  const items: any[] = [];
  
  // 添加子菜单并递归处理
  if (section.subsections && section.subsections.length > 0) {
    section.subsections.forEach(subsection => {
      items.push(...processItems([subsection]));
    });
  }
  
  // 添加最外层按钮
  if (section.buttons && section.buttons.length > 0) {
    section.buttons.forEach(button => {
      items.push({
        ...button,
        type: 'button'
      });
    });
  }
  
  return items as MenuItem[];
};

// 文档数据结构 - 统一的数据格式，通过type属性区分不同类型的元素
const sections = ref<Section[]>([
  {
    title: '产品手册',
    items: [
      // 菜单项类型 - 支持children嵌套
      {
        type: 'button', title: '北境骑团骑行头盔说明书', link: 'https://wwuw.lanzoub.com/i0TjH39gujeh', password: 'bjqt' 
      }
    ]
  }
]);
// const sections = ref<Section[]>([
//   {
//     title: '产品手册',
//     items: [
//       // 菜单项类型 - 支持children嵌套
//       {
//         type: 'menu',
//         title: '山地自行车系列',
//         children: [
//           { 
//             type: 'menu',
//             title: '硬尾山地车',
//             children: [
//               { type: 'menu', title: 'XC硬尾山地车使用指南' },
//               { type: 'menu', title: 'AM硬尾山地车维护手册' },
//               // 按钮类型 - 带下载链接
//               { type: 'button', title: '硬尾山地车技术规格书', link: 'https://www.lanzou.com/hardtail-spec', password: '1234' }
//             ]
//           },
//           { 
//             type: 'menu',
//             title: '全避震山地车',
//             children: [
//               { type: 'menu', title: 'Enduro山地车操作说明' },
//               { type: 'menu', title: 'DH山地车保养手册' },
//               // 按钮类型 - 带下载链接
//               { type: 'button', title: '全避震系统调校指南', link: 'https://www.lanzou.com/full-suspension', password: '5678' }
//             ]
//           },
//           // 按钮类型 - 带下载链接
//           { 
//             type: 'button',
//             title: '山地车通用零部件详解',
//             link: 'https://www.lanzou.com/components-guide', 
//             password: '9012'
//           },
//         ]
//       },
//       {
//         type: 'menu',
//         title: '公路自行车系列',
//         children: [
//           { type: 'menu', title: '耐力型公路车使用指南' },
//           { type: 'menu', title: '竞速型公路车维护手册' },
//           // 按钮类型 - 带下载链接
//           { type: 'button', title: '公路车气动优化指南', link: 'https://www.lanzou.com/aero-guide', password: 'abcd' }
//         ],
//         // 菜单本身也是一个按钮 - 支持下载
//         link: 'https://www.lanzou.com/road-bike-guide', 
//         password: 'efgh'
//       },
//       {
//         type: 'menu',
//         title: '电动自行车系列',
//         children: [
//           { type: 'menu', title: '电动助力自行车操作说明' },
//           // 按钮类型 - 带下载链接
//           { type: 'button', title: '电池维护与保养PDF', link: 'https://www.lanzou.com/battery-care', password: 'ijkl' },
//           { type: 'menu', title: '电机系统故障排查' }
//         ]
//       },
//       // 直接的按钮类型项目
//       { type: 'button', title: '产品手册合集下载', link: 'https://www.lanzou.com/example1', password: 'abcd' },
//       { type: 'button', title: '维修图解PDF', link: 'https://www.lanzou.com/example2', password: 'efgh' }
//     ]
//   },
//   {
//     title: '骑行指南',
//     items: [
//       {
//         type: 'menu',
//         title: '骑行技术',
//         children: [
//           {
//             type: 'menu',
//             title: '基础骑行技巧',
//             children: [
//               { type: 'menu', title: '正确骑行姿势' },
//               { type: 'menu', title: '变速技巧详解' },
//               // 按钮类型 - 带下载链接
//               { type: 'button', title: '基础技巧视频教程', link: 'https://www.lanzou.com/basic-skills', password: 'mnop' }
//             ],
//             // 菜单本身也是一个按钮 - 支持下载
//             link: 'https://www.lanzou.com/basic-technique', 
//             password: 'qrst'
//           },
//           {
//             type: 'menu',
//             title: '进阶骑行技术',
//             children: [
//               { type: 'menu', title: '爬坡技巧' },
//               { type: 'menu', title: '下坡控制' },
//               { type: 'menu', title: '过弯技术' }
//             ]
//           }
//         ],
//         // 菜单本身也是一个按钮 - 支持下载
//         link: 'https://www.lanzou.com/riding-technique', 
//         password: 'uvwx'
//       },
//       {
//         type: 'menu',
//         title: '骑行准备',
//         children: [
//           { type: 'menu', title: '骑行装备选择' },
//           // 按钮类型 - 带下载链接
//           { type: 'button', title: '装备选择指南PDF', link: 'https://www.lanzou.com/equipment-guide', password: 'yz12' },
//           { type: 'menu', title: '路线规划' }
//         ]
//       },
//       // 直接的按钮类型项目
//       { type: 'button', title: '骑行技能进阶PDF', link: 'https://www.lanzou.com/example3', password: 'ijkl' },
//       { type: 'button', title: '安全骑行指南', link: 'https://www.lanzou.com/example8', password: '3456' }
//     ]
//   },
//   {
//     title: '技术文档',
//     items: [
//       {
//         type: 'menu',
//         title: '部件详解',
//         children: [
//           // 按钮类型 - 带下载链接
//           { 
//             type: 'button',
//             title: '传动系统原理',
//             link: 'https://www.lanzou.com/drivetrain-principles', 
//             password: '3456'
//           },
//           { type: 'menu', title: '刹车系统类型' },
//           { type: 'menu', title: '避震系统调校' }
//         ]
//       },
//       {
//         type: 'menu',
//         title: '维护保养',
//         children: [
//           {
//             type: 'menu',
//             title: '日常维护',
//             children: [
//               { type: 'menu', title: '清洁与润滑' },
//               // 按钮类型 - 带下载链接
//               { type: 'button', title: '链条润滑指南', link: 'https://www.lanzou.com/chain-lubrication', password: '7890' },
//               { type: 'menu', title: '刹车调整' }
//             ]
//           },
//           {
//             type: 'menu',
//             title: '定期保养',
//             children: [
//               { type: 'menu', title: '链条更换指南' },
//               { type: 'menu', title: '轴承保养' }
//             ],
//             // 菜单本身也是一个按钮 - 支持下载
//             link: 'https://www.lanzou.com/regular-maintenance', 
//             password: 'qwer'
//           }
//         ]
//       },
//       // 直接的按钮类型项目
//       { type: 'button', title: '技术参数手册', link: 'https://www.lanzou.com/example4', password: 'mnop' },
//       { type: 'button', title: '系统兼容性文档', link: 'https://www.lanzou.com/example5', password: 'qrst' }
//     ]
//   },
//   {
//     title: '品牌资料',
//     items: [
//       // 按钮类型 - 带下载链接
//       { 
//         type: 'button',
//         title: '品牌故事',
//         link: 'https://www.lanzou.com/brand-story', 
//         password: 'asdf'
//       },
//       { type: 'menu', title: '设计理念' },
//       {
//         type: 'menu',
//         title: '赞助计划',
//         children: [
//           // 按钮类型 - 带下载链接
//           { 
//             type: 'button',
//             title: '车队赞助',
//             link: 'https://www.lanzou.com/team-sponsorship', 
//             password: 'zxcv'
//           },
//           { type: 'menu', title: '赛事合作' },
//           { type: 'menu', title: '社区支持' }
//         ]
//       },
//       // 直接的按钮类型项目
//       { type: 'button', title: '品牌形象手册', link: 'https://www.lanzou.com/example6', password: 'uvwx' }
//     ]
//   }
// ]);

// 弹窗状态
const showModal = ref<boolean>(false);
const currentDownload = ref<DownloadItem | null>(null);

// 打开下载弹窗
const openDownloadModal = (button: DownloadItem): void => {
  currentDownload.value = button;
  showModal.value = true;
};

// 关闭弹窗
const closeModal = (): void => {
  showModal.value = false;
  currentDownload.value = null;
};
</script>

<style scoped>
/* 弹窗过渡动画 */
.modal-enter-active,
.modal-leave-active {
  transition: opacity 0.3s ease;
}

.modal-enter-from,
.modal-leave-to {
  opacity: 0;
}

/* 折叠动画 */
.collapse-enter-active,
.collapse-leave-active {
  transition: all 0.3s ease;
  overflow: hidden;
}

.collapse-enter-from,
.collapse-leave-to {
  max-height: 0;
  opacity: 0;
}

.collapse-enter-to,
.collapse-leave-from {
  max-height: 1000px;
  opacity: 1;
}

/* 页码样式 */
.font-serif {
  font-family: Georgia, serif;
}
</style>